import Debug from 'debug';
import { v4 as uuid } from 'uuid';

type StoredSessionData = {
  id: string;
  last: number;
  environment?: string;
};

/**
 * Models an expirable session ID
 *
 * We do not have access to a useable session storage mechanism,
 * so this class uses activity time to determine if the ID is still
 * valid.
 */
export class Session {
  private debug = Debug('o365:Session');
  private id = uuid();
  private environment = 'development';
  private sessionKey = 'bombbomb-sessionid';
  private lastActivity = Date.now();
  private expirationInterval = 1000 * 60 * 60;
  private saveTimeout: number | null = null;

  constructor() {
    // this.environment = LocalStorageSettingsService.getSettings().environment;

    const storedId = localStorage.getItem(this.sessionKey);
    if (storedId) {
      try {
        const parsed = JSON.parse(storedId) as StoredSessionData;
        this.id = parsed.id;
        this.lastActivity = parsed.last;
        if (parsed.environment && parsed.environment !== this.environment) {
          this.debug('stored environment mismatch', parsed.environment, this.environment);
          this.createNewId();
        } else if (this.expired()) {
          this.createNewId();
        } else {
          this.debug(
            'reuse stored session ID. expires ',
            new Date(this.lastActivity + this.expirationInterval),
          );
        }
      } catch (err) {
        this.debug('Error retrieving session ID from local storage', err);
      }
    }
    this.save();
  }

  createNewId(): void {
    this.debug('createNewId');
    this.id = uuid();
    this.save();
  }

  debounceSave(): void {
    if (this.saveTimeout) {
      window.clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    this.saveTimeout = window.setTimeout(() => this.save(), 1000);
  }

  expired(): boolean {
    const expirationDate = Date.now() - this.expirationInterval;
    if (this.lastActivity < expirationDate) {
      return true;
    }
    return false;
  }

  getId(): string {
    if (this.expired()) {
      this.createNewId();
    }
    return this.id;
  }

  save(): void {
    localStorage.setItem(
      this.sessionKey,
      JSON.stringify({
        id: this.id,
        last: this.lastActivity,
        environment: this.environment,
      } as StoredSessionData),
    );
    this.debug('Saved session ID', this.id);
  }

  setActive(): void {
    if (this.expired()) {
      this.createNewId();
    }
    this.lastActivity = Date.now();
    this.debounceSave();
  }
}
